import React, { FC, useEffect, useState } from 'react';
import Lottie from 'lottie-react';
import { VoidFn } from 'types/baseTypes';
import { CustomInput, If } from 'components';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { AttachFileIcon, BackIcon, SendLineIcon } from 'assets/icons';

import JobTimeline from './JobTimeline';
import { JobTimelineEvent, JobTimelineEventType } from '../jobs.model';
import { useCreateNoteMutation } from '../api';
import { logAnalyticEvent } from 'utils/analytics';
import { JobsCleverTapEvents } from '../events';

interface JobHistoryPanelProps {
  onBackClick: VoidFn;
  jobTimeline: JobTimelineEvent[];
  jobId: string;
  teamId: number;
  getJobTimeline: VoidFn;
  onEditNote: (event: JobTimelineEvent) => void;
  onAddNote: VoidFn;
  onDeleteNote: (event: JobTimelineEvent) => void;
  showPreview: (index: number, event: JobTimelineEvent) => void;
  notesInput: string;
  setNotesInput: (note: string) => void;
}

const historyTabs = [
  {
    label: 'All',
    value: 'all',
    filters: []
  },
  {
    label: 'Notes',
    value: 'notes',
    filters: [JobTimelineEventType.Note, JobTimelineEventType.JobNote]
  },
  {
    label: 'Actions',
    value: 'actions',
    filters: [JobTimelineEventType.JobAction, JobTimelineEventType.JobAttachment]
  }
];

const JobHistoryPanel: FC<JobHistoryPanelProps> = ({
  onBackClick,
  jobTimeline,
  jobId,
  teamId,
  getJobTimeline,
  onEditNote,
  onAddNote,
  onDeleteNote,
  showPreview,
  notesInput,
  setNotesInput
}) => {
  const [currentTab, setCurrentTab] = useState(historyTabs[0]);
  const [filteredTimeline, setFilteredTimeline] = useState<JobTimelineEvent[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [currentNotesActionsPopoverId, setCurrentNotesActionsPopoverId] = useState(null);

  const [createNoteMutation, createNoteState] = useCreateNoteMutation();
  const { isLoading: isCreateNoteLoading, isSuccess: isCreateNoteSuccess } = createNoteState;

  useEffect(() => {
    logAnalyticEvent(JobsCleverTapEvents.webJobHistoryPanel);
  }, []);

  useEffect(() => {
    if (jobTimeline) {
      setFilteredTimeline(jobTimeline);
      setCurrentTab(historyTabs[0]);
      setIsLoading(false);
    }
  }, [jobTimeline]);

  useEffect(() => {
    if (!isCreateNoteLoading && isCreateNoteSuccess) {
      getJobTimeline();
    }
  }, [isCreateNoteSuccess, isCreateNoteLoading]);

  const onSendNoteClick = () => {
    if (!notesInput) return;
    setIsLoading(true);
    createNoteMutation({ comment: notesInput, attachments: [], jobId, teamId });
    setNotesInput('');
  };

  const handleTabChange = tab => {
    if (tab.value === currentTab.value) return;
    setCurrentTab(tab);
    if (tab.value === 'all') setFilteredTimeline(jobTimeline);
    else setFilteredTimeline(jobTimeline.filter(event => tab.filters.includes(event.type)));
  };

  const handleAnalyticEventForNotes = () => {
    logAnalyticEvent(JobsCleverTapEvents.webEnterNotesTextBoxClicked);
  };

  // eslint-disable-next-line react/no-multi-comp
  const NotesInputSuffix = () => (
    <div className="flex items-center gap-x-4">
      <AttachFileIcon className="shrink-0 cursor-pointer" onClick={onAddNote} />
      <SendLineIcon className="shrink-0 cursor-pointer" width={18} height={18} onClick={onSendNoteClick} />
    </div>
  );

  return (
    <div className="h-full w-96">
      <If condition={isLoading}>
        <div className="flex h-full w-96 items-center justify-center">
          <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
        </div>
      </If>
      {!isLoading && (
        <div className="flex h-full flex-col">
          <div className="flex items-center py-4">
            <BackIcon className="mx-4 cursor-pointer" id="job-details-panel-back-arrow" onClick={onBackClick} />
            <div className="text-17px font-semibold text-headingGray">History</div>
          </div>
          <div className="flex h-full flex-col">
            <div className="flex gap-x-2.5 border border-secondaryBtn p-4">
              {historyTabs.map(tab => (
                <div
                  key={`job-history-${tab.value}-tab`}
                  className={`flex w-[60px] cursor-pointer justify-center rounded-full border py-1.5 text-px13
              text-heading ${
                currentTab.value === tab.value
                  ? 'border-primaryBtn bg-secondaryBtn font-semibold'
                  : 'border-gray-1 bg-white'
              }`}
                  onClick={() => handleTabChange(tab)}>
                  {tab.label}
                </div>
              ))}
            </div>
            <div className="relative flex h-full flex-col pb-[220px]">
              <JobTimeline
                jobTimeline={filteredTimeline}
                onEditNoteClick={onEditNote}
                onDeleteNoteClick={onDeleteNote}
                hasImageViewer={true}
                showPreview={showPreview}
                timelineId="jobs-history-timeline"
                currentActionsPopoverId={currentNotesActionsPopoverId}
                setCurrentActionsPopoverId={setCurrentNotesActionsPopoverId}
              />
              <div className="fixed bottom-0 w-[383px]">
                <div className="mb-0.5 w-full border border-borderGray py-6 px-4">
                  <CustomInput
                    id="job-history-notes-input"
                    value={notesInput}
                    onChange={setNotesInput}
                    hasSuffix={true}
                    showSuffixAlways={true}
                    suffix={<NotesInputSuffix />}
                    className="h-12 rounded-md border border-borderGray bg-transparent pr-[72px]
                          placeholder:font-semibold focus:outline-none"
                    suffixStyle="right-4 top-4"
                    placeholder="Enter note"
                    onClick={handleAnalyticEventForNotes}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobHistoryPanel;
